import * as React from 'react'
import { OwnerListOption } from './types'
import { useEffect } from 'react'
import { Field } from 'formik'
import { useData, useHandlers } from './hooks'
import type { ENTITY_TYPES } from '@sales-center/common'
import Autocomplete from '@mui/material/Autocomplete'
import { TextField } from '@workwave-tidal/core/components'
import MenuItem from '@mui/material/MenuItem'
import { useTidalThemeActive } from '@user/features/TidalThemeToggle'
import FormHelperText from '@mui/material/FormHelperText'
import { LeadAssignmentRule } from '@user/components/Settings/LeadAssignmentRule/LeadAssignmentRuleForm/typings'

type Props = {
  entityType?: ENTITY_TYPES
  errorText?: string
  fieldProps?: any
  salesTeamId: string
  size?: string
  disabled?: boolean
  name: string
  label?: string
  margin?: string
  color?: string
  owners?: { id?: string; userId?: string; name: string }[]
  required?: boolean
  defaultValue?: string
  useAdditionalMargin?: boolean
  unassignedOption?: boolean
  testId?: string
  /**
   * Receives the selected OPTION, not a DOM change event -- useHandlers calls `onChange(value)`
   * with the option object. It was typed as a ChangeEvent, which is why every caller that needed
   * the selection reached for `any` to get at `.id`.
   */
  onChange?: (value?: OwnerListOption) => void
  noTouchValidation?: boolean
  showInactiveUsers?: boolean
  defaultSetRequired?: boolean
  ignoreDefault?: boolean
  assignmentRuleData?: {
    rule: LeadAssignmentRule | null
    canOverride: boolean
  }
}

export function OwnerSelect(props: Props) {
  const {
    fieldProps = {},
    disabled = false,
    size = 'medium',
    name,
    label = 'Owner',
    margin = 'none',
    color = 'primary',
    required = true,
    defaultValue = null,
    useAdditionalMargin = false,
    testId,
    onChange,
    noTouchValidation = false,
    errorText,
    defaultSetRequired = true,
    ignoreDefault = false,
  } = props
  const tidalThemeActive = useTidalThemeActive()

  const { handleChange, handleOnBlur } = useHandlers({
    name,
    onChange,
    noTouchValidation,
  })
  const {
    options,
    helperText,
    showError,
    menuItemStyle,
    value,
    setFieldValue,
  } = useData({
    ...props,
  })

  useEffect(() => {
    if (options.length <= 1) return

    if (defaultValue != null && !ignoreDefault) {
      setFieldValue(name, defaultValue)
    }
    if (
      defaultValue == null &&
      options?.length == 1 &&
      defaultSetRequired &&
      !ignoreDefault
    ) {
      setFieldValue(name, options[0].id)
    }
  }, [
    defaultValue,
    name,
    setFieldValue,
    options.length,
    defaultSetRequired,
    ignoreDefault,
  ])

  const height = tidalThemeActive ? '50px' : '33px'

  return (
    <>
      <Field
        sx={{ margin: useAdditionalMargin ? '16px 0px 8px 0px' : '0' }}
        value={value || null}
        label={label}
        name={name}
        component={Autocomplete}
        disabled={disabled}
        fullWidth
        margin={margin}
        required={required}
        data-test-id="owner-select"
        options={options}
        onChange={handleChange}
        getOptionLabel={(option) => option?.name || ''}
        getOptionDisabled={(option) => !option.id}
        isOptionEqualToValue={(option, value) =>
          option?.id === (value.id || value)
        }
        size={size}
        color={color}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            avoidDisplayingErrorIcon
            error={showError}
            required={required}
            onBlur={noTouchValidation ? null : handleOnBlur}
            disabled={disabled}
            {...fieldProps}
            inputProps={{
              ...params.inputProps,
              name: name,
              'data-test-id': testId,
            }}
          />
        )}
        renderOption={({ key, ...props }, option) => (
          <MenuItem
            sx={{
              height: height,
              minHeight: height,
              ...menuItemStyle.root,
            }}
            key={`${option.idx}_owner`}
            value={option.id}
            disabled={option.isPosition || !option.id}
            {...props}
          >
            <span style={{ width: `${option.level * 10}px` }}></span>
            {option.name}
          </MenuItem>
        )}
        {...fieldProps}
      />

      {(showError || errorText) && (
        <FormHelperText sx={{ ml: 1 }} error>
          {errorText || helperText}
        </FormHelperText>
      )}
    </>
  )
}
